<template>
  <div class="ew-auth">
    <div class="ew-auth__panel">
      <div class="ew-auth__card">
        <router-link to="/">
          <img class="ew-auth__logo" src="@/assets/images/logo-ew.png" alt="Logo Moovet">
        </router-link>

        <h1 class="ew-auth__title">Bienvenido de nuevo</h1>
        <p class="ew-auth__subtitle">Ingresa tus datos para continuar con tus envíos.</p>

        <p class="alert alert-success ew-alert" v-if="newUser">Tu cuenta ha sido creada. Inicia sesión con tus credenciales</p>
        <p class="alert alert-danger ew-alert" role="alert" v-if="signinError">Correo y/o contraseña inválidos</p>
        <p class="alert alert-danger ew-alert" role="alert" v-if="sessionExpired">Tu sesión expiró. Inicia sesión de nuevo para continuar.</p>
        <p class="alert alert-warning ew-alert" role="alert" v-if="idleLogout">Cerramos tu sesión por inactividad.</p>

        <form @submit.prevent="signin" novalidate>
          <div class="ew-form__field">
            <label class="ew-label" for="si-email">Correo electrónico *</label>
            <div class="ew-input-group">
              <span class="ew-input-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M4 7l8 6 8-6"/></svg>
              </span>
              <input
                id="si-email"
                type="email"
                class="ew-input ew-input--block ew-input--icon"
                placeholder="correo@ejemplo.com"
                v-model.trim="email"
                required
                :disabled="isLoading"
                @input="$v.email.$touch()"
                :class="{ 'is-invalid': $v.email.$error }"
              >
            </div>
            <div class="ew-field-error" v-if="$v.email.$error">Debes introducir un correo electrónico válido</div>
          </div>

          <div class="ew-form__field">
            <div class="ew-label-row">
              <label class="ew-label" for="si-password">Contraseña *</label>
              <router-link class="ew-auth__forgot" to="/password-recovery">¿La olvidaste?</router-link>
            </div>
            <div class="ew-input-group">
              <span class="ew-input-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>
              </span>
              <input
                id="si-password"
                :type="showPassword ? 'text' : 'password'"
                class="ew-input ew-input--block ew-input--icon ew-input--icon-end"
                placeholder="Tu contraseña"
                v-model.trim="password"
                required
                :disabled="isLoading"
                @input="$v.password.$touch()"
                :class="{ 'is-invalid': $v.password.$error }"
              >
              <button
                type="button"
                class="ew-input-toggle"
                @click="showPassword = !showPassword"
                :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
              >
                <svg v-if="showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3l18 18"/><path d="M10.6 5.2A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a17.7 17.7 0 0 1-3.2 4.1M6.5 6.6C3.8 8.3 2 12 2 12s3.5 7 10 7a10.5 10.5 0 0 0 4.2-.9"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/></svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
              </button>
            </div>
            <div class="ew-field-error" v-if="$v.password.$error">
              La contraseña debe tener una longitud de entre {{ $v.password.$params.minLength.min }} y {{ $v.password.$params.maxLength.max }} caracteres
            </div>
          </div>

          <button type="submit" class="ew-btn-primary ew-auth__submit" :class="{ 'is-loading': isLoading }" :disabled="$v.$invalid || isLoading">
            <span class="ew-spinner" v-if="isLoading" aria-hidden="true"></span>
            <span>{{ isLoading ? 'Ingresando…' : 'Ingresar' }}</span>
          </button>
        </form>

        <p class="ew-auth__hint ew-auth__hint--footer">
          ¿Nuevo en Moovet?
          <router-link to="/signup">Crea tu cuenta aquí</router-link>
        </p>
      </div>
    </div>

    <div class="ew-auth__brand">
      <span class="ew-auth__brand-ring ew-auth__brand-ring--a" aria-hidden="true"></span>
      <span class="ew-auth__brand-ring ew-auth__brand-ring--b" aria-hidden="true"></span>

      <div class="ew-auth__brand-content">
        <div class="ew-auth__illustration">
          <div class="ew-auth__illustration-canvas">
            <!--
              Un solo trazo continuo que pasa POR DETRÁS de cada tarjeta (las
              tarjetas y la insignia van encima en z-index, así que el tramo
              que cruza cada una queda oculto): sale de "Cotiza" por abajo a
              la izquierda, da la vuelta por la izquierda, entra a "Genera" por
              arriba y sale por su derecha, sube a "Rastrea" por abajo y sale
              por arriba, y termina detrás de la insignia. Los puntos naranja
              van en el punto medio calculado de cada tramo visible.
            -->
            <svg class="ew-auth__route" viewBox="0 0 550 340" aria-hidden="true">
              <path class="ew-auth__route-line" d="M150,40 C135,95 70,110 70,160 C70,210 145,280 240,280 C295,280 335,235 362,200 C387,170 407,150 422,140 C442,125 380,80 275,125" fill="none" />
            </svg>

            <span class="ew-auth__route-dot" style="left: 70px; top: 160px; animation-delay: 0s" aria-hidden="true"></span>
            <span class="ew-auth__route-dot" style="left: 395px; top: 110px; animation-delay: .4s" aria-hidden="true"></span>
            <span class="ew-auth__route-dot" style="left: 312px; top: 253px; animation-delay: .8s" aria-hidden="true"></span>

            <div class="ew-auth__hero-badge" aria-hidden="true">
              <ph-icon name="package" />
            </div>

            <div
              class="ew-auth__route-card"
              :class="'ew-auth__route-card--' + i"
              v-for="(step, i) in brandSteps"
              :key="step.title"
              :style="{ animationDelay: (0.5 + i * 0.15) + 's' }"
            >
              <span class="ew-auth__step-icon" :style="{ animationDelay: (0.9 + i * 0.4) + 's' }">
                <ph-icon :name="step.icon" />
              </span>
              <span class="ew-auth__step-copy">
                <strong>{{ step.title }}</strong>
                <span>{{ step.text }}</span>
              </span>
            </div>
          </div>
        </div>

        <h2 class="ew-auth__headline">Todos tus envíos, bajo control.</h2>
        <p class="ew-auth__brand-text">Compara tarifas de DHL, Estafeta, FedEx y más, genera tus guías y rastrea cada paquete desde un solo panel.</p>
      </div>
    </div>

    <a class="ew-call" href="tel:3317547337" aria-label="Llámanos">
      <img src="@/assets/images/call-answer.png" alt="">
    </a>
  </div>
</template>

<script>
import axios from 'axios'
import commons from '../commons'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import PhIcon from './icons/PhIcon'
import {
  required,
  minLength,
  maxLength,
  email
} from 'vuelidate/lib/validators'

export default {
  name: 'SignIn',
  data () {
    return {
      email: '',
      password: '',
      showPassword: false,
      signinError: false,
      newUser: false,
      sessionExpired: false,
      idleLogout: false,
      isLoading: false,
      // Orden = posición en la ilustración: 0 arriba, 1 a la derecha
      // (junto a la insignia central), 2 abajo a la izquierda — ver
      // .ew-auth__route-card--N para las coordenadas de cada una.
      brandSteps: [
        { icon: 'chart-line-up', title: 'Cotiza al instante', text: 'Compara tarifas y elige' },
        { icon: 'map-pin', title: 'Rastrea cada envío', text: 'Estatus en tiempo real' },
        { icon: 'package', title: 'Genera tu guía', text: 'Descárgala e imprímela' }
      ]
    }
  },
  created () {
    if (localStorage.getItem('ew-user-id')) {
      this.$router.push(this.$route.query.redirect || '/dashboard')
      console.log('you are logged in')
    }

    this.newUser = this.$route.query.newUser
    this.sessionExpired = this.$route.query.expired === 'true'
    this.idleLogout = this.$route.query.idle === 'true'
  },
  methods: {
    signin () {
      this.isLoading = true
      this.signinError = false
      axios
        .post(`${commons.apiURL}/api/v1/login`, {
          email: this.email,
          password: this.password
        })
        .then(response => {
          localStorage.setItem('ew-user-id', response.data.id)
          localStorage.setItem('ew-user-token', response.data.token)

          // Si el guard de router.beforeEach mandó aquí porque intentaron
          // entrar a una ruta del dashboard sin sesión, de regreso a esa
          // misma ruta en vez de siempre a /dashboard.
          this.$router.push(this.$route.query.redirect || '/dashboard')
        })
        .catch(error => {
          console.log(error.response)
          if (error.response.status === 404) {
            this.signinError = true
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    ...mapActions(['quoting', 'newShipment', 'getAllShipments']),
    ...mapMutations(['setNewShipmentRequestBody'])
  },
  computed: {
    ...mapGetters(['newShipmentRequestBody'])
  },
  validations: {
    email: {
      required,
      email,
      maxLength: maxLength(35)
    },
    password: {
      required,
      minLength: minLength(4),
      maxLength: maxLength(12)
    }
  },
  components: {
    'ph-icon': PhIcon
  }
}
</script>

<style scoped lang="scss">
@import "../assets/styles/app-tokens";
@import "../assets/styles/ew-components";

.ew-auth { @include app-tokens; @include ew-auth-shell; }
.ew-auth__panel { @include ew-auth-panel; }

.ew-auth__card {
  @include ew-auth-card;
  animation: ew-fade-up .55s ease-out both;
}

.ew-auth__logo { @include ew-auth-logo; }

.ew-auth__title {
  margin: 0 0 0.4rem;
  font-family: var(--app-font);
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--app-navy);
}
.ew-auth__subtitle {
  margin: 0 0 1.75rem;
  font-family: var(--app-font);
  font-size: 0.92rem;
  color: var(--app-muted);
}

.ew-auth__hint {
  @include ew-auth-hint;
  &--footer { margin: 1.75rem 0 0; text-align: center; }
}

.ew-alert { font-family: var(--app-font); border-radius: var(--app-radius-sm); margin-bottom: 1.25rem; }

.ew-form__field { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 1.25rem; }
.ew-label { font-family: var(--app-font); font-size: 0.8rem; font-weight: 700; color: var(--app-navy); }
.ew-label-row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
.ew-auth__forgot {
  font-family: var(--app-font);
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--app-accent-button);
  &:hover { color: var(--app-accent-button-hover); text-decoration: underline; }
}

.ew-input { @include ew-input; }
.ew-input--block { width: 100%; }
.ew-field-error { @include ew-field-error; }

.ew-input-group {
  position: relative;

  &:focus-within .ew-input-icon { color: var(--app-accent-button); }
}
.ew-input-icon {
  position: absolute;
  top: 50%;
  left: 0.85rem;
  transform: translateY(-50%);
  display: flex;
  color: var(--app-muted);
  pointer-events: none;
  transition: color .15s ease;

  svg { width: 1.05rem; height: 1.05rem; }
}
.ew-input--icon { padding-left: 2.6rem; }
.ew-input--icon-end { padding-right: 2.6rem; }

.ew-input-toggle {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--app-muted);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;

  svg { width: 1.05rem; height: 1.05rem; }

  &:hover { background: var(--app-surface-muted); color: var(--app-navy); }
  &:focus-visible { outline: 3px solid var(--app-accent); outline-offset: 2px; }
}

.ew-btn-primary { @include ew-btn-primary; }
.ew-auth__submit {
  width: 100%;
  height: 3rem;
  margin-top: 0.5rem;
  transition: background-color .15s ease, color .15s ease, transform .15s ease;

  &:hover:not(:disabled) { transform: translateY(-1px); }
  &:active:not(:disabled) { transform: translateY(0); }

  // Solo en login por ahora: el mixin compartido ew-btn atenúa el botón
  // deshabilitado con opacity .45 (naranja lavado). Aquí, formulario
  // incompleto = gris neutro; enviando = naranja sólido con el spinner.
  &:disabled:not(.is-loading) {
    opacity: 1;
    background: var(--app-surface-muted);
    color: var(--app-muted);
    cursor: not-allowed;
  }
  &.is-loading:disabled {
    opacity: 1;
    background: var(--app-accent-button);
    color: var(--app-on-accent);
    cursor: progress;
  }
}

.ew-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ew-spin .7s linear infinite;
}

/* Panel de marca ------------------------------------------------------- */
// Reescrito solo para SignIn (no via mixin compartido): SignUp/ChangePassword/
// PasswordRecovery siguen usando ew-auth-brand* tal cual estaban, sin tocar.
.ew-auth__brand {
  position: relative;
  flex: 1 1 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 3.5rem;
  background: linear-gradient(160deg, #ffffff 0%, var(--app-canvas) 45%, #E7EEF6 100%);
  border-left: 1px solid var(--app-border);
  color: var(--app-navy);

  @media (max-width: 900px) { display: none; }
}

.ew-auth__brand-ring {
  position: absolute;
  border: 1px dashed rgba(7, 50, 88, 0.1);
  border-radius: 50%;
  pointer-events: none;
  animation: ew-ring-spin 90s linear infinite;

  &--a { top: -9rem; right: -9rem; width: 26rem; height: 26rem; }
  &--b { bottom: -7rem; left: -7rem; width: 18rem; height: 18rem; border-color: rgba(7, 50, 88, 0.07); animation-duration: 70s; animation-direction: reverse; }
}

.ew-auth__brand-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 33.5rem;
  text-align: center;
}

.ew-auth__headline {
  max-width: 26rem;
  margin: 2.25rem 0 0.6rem;
  font-family: var(--app-font);
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1.3;
  color: var(--app-navy);
  animation: ew-fade-up .55s ease-out .6s both;
}

.ew-auth__brand-text {
  max-width: 27rem;
  margin: 0;
  font-family: var(--app-font);
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--app-muted);
  animation: ew-fade-up .55s ease-out .7s both;
}

// Ilustración de la insignia + 3 tarjetas + camino punteado ----------------
// Reproduce la composición horizontal de la imagen de referencia del cliente:
// tarjeta arriba, insignia central ligeramente por debajo de ella, tarjeta a
// la derecha y tarjeta abajo a la izquierda, unidas por tres curvas punteadas
// con un punto naranja en cada una. Todo está dibujado sobre un lienzo de
// tamaño FIJO (530×330px, coordenadas en píxeles calculadas a mano para que
// cada curva nazca y termine en el borde de su tarjeta) y el lienzo entero se
// escala con transform en ventanas angostas, en vez de comprometer el diseño
// para el caso más estrecho. --ill-scale es el único valor que cambia.
.ew-auth__illustration {
  --ill-scale: 1;
  width: calc(550px * var(--ill-scale));
  height: calc(340px * var(--ill-scale));

  @media (max-width: 1323px) { --ill-scale: 0.9; }
  @media (max-width: 1213px) { --ill-scale: 0.72; }
  @media (max-width: 1015px) { --ill-scale: 0.6; }
}

.ew-auth__illustration-canvas {
  position: relative;
  width: 550px;
  height: 340px;
  transform: scale(var(--ill-scale));
  transform-origin: top left;
}

.ew-auth__route {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}
.ew-auth__route-line {
  stroke: rgba(7, 50, 88, 0.42);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 7 7;
  animation: ew-dash-flow-svg 1s linear infinite;
}

.ew-auth__route-dot {
  position: absolute;
  z-index: 3;
  width: 0.7rem;
  height: 0.7rem;
  margin: -0.35rem;
  border-radius: 50%;
  background: var(--app-accent);
  box-shadow: 0 0 0 4px rgba(246, 58, 14, 0.16), 0 0 0 5px #fff;
  animation: ew-pulse-dot 2.4s ease-in-out infinite;
}

.ew-auth__hero-badge {
  position: absolute;
  z-index: 1;
  top: 80px;
  left: 215px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 1.25rem;
  background: #fff;
  box-shadow: 0 18px 38px rgba(7, 50, 88, 0.16);
  color: var(--app-accent-button);
  animation: ew-fade-up .55s ease-out .34s both, ew-float 4.5s ease-in-out 1s infinite;

  svg { width: 2.4rem; height: 2.4rem; }
}

// Alto fijo (no min-height): con las tarjetas posicionadas de forma
// absoluta, una altura que varíe con el texto podría tapar la insignia o
// encimarse con otra tarjeta. Título y subtítulo van a UNA línea cada uno
// (copy acortado a propósito); 64px alcanza con holgura.
.ew-auth__route-card {
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 240px;
  height: 64px;
  padding: 0 0.95rem;
  background: #fff;
  border-radius: var(--app-radius-md);
  box-shadow: 0 12px 28px rgba(7, 50, 88, 0.1);
  animation: ew-fade-up .5s ease-out both;

  &--0 { top: 0; left: 120px; }
  &--1 { top: 150px; left: 310px; }
  &--2 { top: 250px; left: 0; }
}

.ew-auth__step-icon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 0.7rem;
  background: rgba(7, 50, 88, 0.06);
  border: 1px solid rgba(7, 50, 88, 0.08);
  color: var(--app-navy);
  animation: ew-float 4s ease-in-out infinite;

  svg { width: 1.15rem; height: 1.15rem; }
}

.ew-auth__step-copy {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;

  strong { font-family: var(--app-font); font-size: 0.86rem; font-weight: 700; line-height: 1.25; color: var(--app-navy); white-space: nowrap; }
  span { font-family: var(--app-font); font-size: 0.76rem; line-height: 1.3; color: var(--app-muted); white-space: nowrap; }
}

@keyframes ew-fade-up {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes ew-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
@keyframes ew-pulse-dot {
  0%, 100% { box-shadow: 0 0 0 4px rgba(246, 58, 14, 0.18); }
  50% { box-shadow: 0 0 0 7px rgba(246, 58, 14, 0.08); }
}
@keyframes ew-dash-flow-svg {
  to { stroke-dashoffset: -14; }
}
@keyframes ew-ring-spin {
  to { transform: rotate(360deg); }
}
@keyframes ew-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .ew-auth__card,
  .ew-auth__headline,
  .ew-auth__brand-text,
  .ew-auth__hero-badge,
  .ew-auth__route-card,
  .ew-auth__step-icon,
  .ew-auth__route-dot,
  .ew-auth__route-line,
  .ew-auth__brand-ring,
  .ew-spinner {
    animation: none !important;
  }
}

.ew-call {
  position: fixed;
  bottom: 1.75rem;
  left: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.75rem;
  height: 3.75rem;
  border-radius: 50%;
  background: var(--app-accent-button);
  box-shadow: 0 4px 10px rgba(7, 50, 88, 0.25);

  img { width: 1.5rem; height: auto; }

  &:hover { background: var(--app-accent-button-hover); }
  &:focus-visible { outline: 3px solid var(--app-accent); outline-offset: 2px; }
}

@media (max-width: 400px) {
  .ew-call { width: 3rem; height: 3rem; bottom: 1.2rem; left: 1.2rem; }
  .ew-call img { width: 1.25rem; }
}
</style>



// WEBPACK FOOTER //
// src/components/SignIn.vue